import {
  Patientportal_AppointmentRequestProxyMsg,
  Resource_AppointmentStatus,
} from '@/generated/graphql';
import { TeamOutlined } from '@ant-design/icons';
import { LinkedAppointmentCancelledTag } from '@components/appointments/linked-appointment-cancelled-tag';
import { Avatar, Empty, List, Space } from 'antd';
import { Maybe } from 'graphql/jsutils/Maybe';
import { useRouter } from 'next/router';
import React, { FC } from 'react';
import { useIntl } from 'react-intl';
import { extractTranslation, formatPortalFacilityPath } from '../../helpers/formatters';
import {
  AppointmentLocation,
  buildImageDownloadUrl,
  getAppointmentTypeName,
} from '../../helpers/utils';
import styles from '../../pages/appointments/appointments.module.scss';
import { AveliosButton } from '../common-layout/avelios-button';
import { DateRangeFormatter } from '../common-layout/date-range-formatter';
import { AppointmentRequestStatusTag } from './appointment-request-status-tag';

interface AppointmentRequestListProps {
  appointmentRequestProxies:
    | Maybe<(Maybe<Patientportal_AppointmentRequestProxyMsg> | undefined)[]>
    | undefined;
  appointmentRequestId: Maybe<string> | undefined;
  setAppointmentRequestId: (appointmentProxy: Maybe<string> | undefined) => void;
  loadMore?: () => void;
}

export const AppointmentRequestList: FC<AppointmentRequestListProps> = ({
  appointmentRequestProxies,
  appointmentRequestId,
  setAppointmentRequestId,
  loadMore,
}) => {
  const { formatMessage } = useIntl();

  const router = useRouter();

  return appointmentRequestProxies && appointmentRequestProxies?.length > 0 ? (
    <List<Patientportal_AppointmentRequestProxyMsg>
      dataSource={appointmentRequestProxies?.map((pA) => pA!) || []}
      loadMore={
        loadMore && (
          <AveliosButton onClick={loadMore} type={'link'} block>
            {formatMessage({ id: 'appointments.load-more' })}
          </AveliosButton>
        )
      }
      renderItem={(item: Patientportal_AppointmentRequestProxyMsg) => {
        const facilities = item?.portalFacilityPath?.portalFacilities ?? [];
        const officeHour = facilities?.[facilities.length - 1];
        const appointmentRequest = item?.appointmentRequest;
        const exactSlot = appointmentRequest?.requestPreferences?.exactTimeSlot;
        const insideTimeFrame = appointmentRequest?.requestPreferences?.insideTimeFrame;
        const selected = appointmentRequestId === item.appointmentRequest?.id;
        const hasCancelledAppointments = item?.appointmentRequest?.appointments?.some(
          (a) => a?.appointmentStatus === Resource_AppointmentStatus.AppointmentStatusCancelled,
        );

        const groupAppointment = appointmentRequest?.appointmentType?.groupAppointment;

        const appointmentTypeName = extractTranslation(
          getAppointmentTypeName(appointmentRequest?.appointmentType),
          router.locale,
        );

        const unverified = appointmentRequest?.unverified;
        let title: React.ReactNode = formatMessage({
          id: 'appointment-request',
        });
        if (exactSlot) {
          title = <DateRangeFormatter start={exactSlot?.start} />;
        } else if (insideTimeFrame) {
          title = <DateRangeFormatter start={insideTimeFrame?.start} end={insideTimeFrame.end} />;
        }

        return (
          <List.Item
            className={selected ? styles.listItemSelected : styles.listItem}
            onClick={() => {
              if (appointmentRequestId === item?.appointmentRequest?.id) {
                setAppointmentRequestId(null);
              } else {
                setAppointmentRequestId(item.appointmentRequest?.id);
              }
            }}
          >
            <List.Item.Meta
              avatar={
                <span>
                  {officeHour?.logoImageId ? (
                    <Avatar src={buildImageDownloadUrl(officeHour.logoImageId)} size={50} />
                  ) : (
                    <Avatar shape="circle" size="large" />
                  )}
                </span>
              }
              title={
                <div className={styles.dateTime}>
                  <div>{title}</div>
                  <Space>
                    <AppointmentRequestStatusTag
                      unverified={unverified}
                      status={item?.appointmentRequest?.status}
                    />
                    {hasCancelledAppointments && (
                      <LinkedAppointmentCancelledTag
                        singleAppointment={
                          (item?.appointmentRequest?.appointments?.length ?? 0) <= 1
                        }
                        showIcon
                      />
                    )}
                  </Space>
                </div>
              }
              description={
                <div className={styles.listItemFacilities} style={{ marginTop: 10 }}>
                  <div
                    style={{
                      color: 'black',
                      fontSize: '1.2em',
                    }}
                  >
                    {appointmentTypeName}
                  </div>
                  <AppointmentLocation
                    location={item?.appointmentRequest?.requestPreferences?.location}
                  />
                  {groupAppointment && (
                    <div>
                      <TeamOutlined />{' '}
                      {formatMessage({ id: 'appointment-request.group-appointment' })}
                    </div>
                  )}
                  <div>{formatPortalFacilityPath(facilities)}</div>
                </div>
              }
            />
          </List.Item>
        );
      }}
      rowKey={(proxyAppointment) => proxyAppointment!.appointmentRequest!.id!}
    />
  ) : (
    <Empty
      description={formatMessage({
        id: 'appointments-requested.empty',
      })}
    />
  );
};
